import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DataModule from '../DataModules';
import { tokens } from '../../../global/theme/tokens';

/**
 * Contact data, address and private data combined into one tab. The API only
 * delivers the address and the private block to viewers with the respective
 * permissions, so each section simply renders when its data is present —
 * nobody sees more than they are allowed to.
 */
const EmployeePersonData = ({ employeeInfo }) => {
  const { t } = useTranslation();

  const address = employeeInfo.address;
  const personal = employeeInfo.personal;

  const contactModules = [
    {
      type: 'mail',
      label: t('Business Mail'),
      value: employeeInfo.email,
    },
    {
      type: 'mail',
      label: t('Private Mail'),
      value: employeeInfo.privateMail,
    },
    {
      type: 'phone',
      label: t('Business Phone'),
      value: employeeInfo.phone,
    },
    {
      type: 'phone',
      label: t('Private Phone'),
      value: employeeInfo.privatePhone,
    },
  ];

  const addressLine = address
    ? [
        [address.street, address.houseNumber].filter(Boolean).join(' '),
        address.addressAddition,
        [address.zipCode, address.city].filter(Boolean).join(' '),
        address.country,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const personalModules = personal
    ? [
        { type: 'date', label: t('Birthday'), value: personal.birthday },
        { type: 'text', label: t('Birthplace'), value: personal.birthplace },
        { type: 'text', label: t('Nationality'), value: personal.nationality },
        { type: 'text', label: t('Gender'), value: personal.gender },
        {
          type: 'text',
          label: t('Family Status'),
          value: personal.familyStatus,
        },
        {
          type: 'text',
          label: t('Parental Property'),
          value: personal.parentalProperty ? 'Yes' : 'No',
        },
        { type: 'text', label: t('Religion'), value: personal.religion },
        {
          type: 'text',
          label: t('Highest Degree'),
          value: personal.highestDegree,
        },
        {
          type: 'text',
          label: t('Highest Professional Education'),
          value: personal.highestProfessionalEducation,
        },
        {
          type: 'text',
          label: t('Severely Disabled'),
          value: personal.severelyDisabled ? 'Yes' : 'No',
        },
      ]
    : [];

  return (
    <Box className="flex flex-col gap-6 items-start">
      <Section label={t('Contact Data')}>
        {contactModules.map((display, index) => (
          <DataModule
            key={index}
            type={display.type}
            label={display.label}
            value={display.value}
          />
        ))}
      </Section>

      {address && (
        <Section label={t('Address')}>
          <DataModule type="text" label={t('Address')} value={addressLine} />
        </Section>
      )}

      {personal && (
        <Section label={t('Private')}>
          {personalModules.map((display, index) => (
            <DataModule
              key={index}
              type={display.type}
              label={display.label}
              value={display.value}
            />
          ))}
        </Section>
      )}
    </Box>
  );
};

const Section = ({ label, children }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box className="flex flex-col gap-3 items-start w-full">
      <Box
        className="flex items-center gap-2 w-full"
        sx={{ borderBottom: `1px solid ${colors.selected}` }}
      >
        <Typography variant="h6" className="opacity-70 pb-1">
          {label}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

export default EmployeePersonData;
